// Omertàbella brand mark — refactored to native text + transparent monogram.
//
// Why: a single raster lockup (monogram+wordmark+tagline baked into one PNG)
// always reads as a "sticker" against the page. We render the monogram as a
// transparent-bg image and the wordmark+tagline as native, letter-spaced text
// so the brand mark scales crisply and integrates typographically.
//
// Components:
//   <Monogram .../>           — transparent OB graphic (image)
//   <Wordmark .../>           — native Playfair Display caps text
//   <Tagline .../>            — native Inter caps text
//   <BrandLockup .../>        — monogram + wordmark + tagline composed (use on hero)
//   <BrandLockupHorizontal/>  — monogram + wordmark inline (use in headers, footer)
//
// Legacy raster <LogoFull/>, <LogoWordmark/>, <LogoMonogram/> exports kept as
// passthroughs for any in-flight references but should be migrated.

import monoTransparent from "../../assets/brand/logo_monogram_transparent.png";
import wordmarkTransparent from "../../assets/brand/logo_wordmark_transparent.png";
import fullTransparent from "../../assets/brand/logo_full_transparent.png";

// ---- New atomic primitives ----
export function Monogram({ className = "h-16 w-auto", glow = false, ...rest }) {
  return (
    <div className={`relative inline-flex items-center justify-center ${glow ? "after:absolute after:inset-0 after:-z-10 after:rounded-full after:bg-brand-gold/[0.12] after:blur-[80px]" : ""}`}>
      <img
        src={monoTransparent}
        alt="Omertàbella"
        loading="eager"
        decoding="async"
        className={`block ${className}`}
        {...rest}
      />
    </div>
  );
}

export function Wordmark({ as: As = "span", className = "", size = "md" }) {
  const sizeMap = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-xl",
    xl: "text-3xl sm:text-4xl",
  };
  return (
    <As
      className={`font-display font-normal uppercase tracking-[0.4em] text-brand-text ${sizeMap[size] || sizeMap.md} ${className}`}
    >
      Omert<span className="font-display">à</span>bella
    </As>
  );
}

export function Tagline({ className = "", muted = true }) {
  return (
    <span
      className={`block uppercase tracking-[0.3em] ${muted ? "text-brand-text/70" : "text-brand-text"} ${className}`}
    >
      Private Influence · Public Power
    </span>
  );
}

/** Full vertical lockup — monogram above wordmark above tagline. Use on hero. */
export function BrandLockup({
  monogramClassName = "h-32 sm:h-40 lg:h-44",
  wordmarkSize = "lg",
  taglineClassName = "text-[10px] sm:text-xs",
  glow = true,
  className = "",
}) {
  return (
    <div className={`flex flex-col items-center ${className}`}>
      <Monogram className={monogramClassName} glow={glow} />
      <Wordmark className="mt-8 text-center" size={wordmarkSize} />
      <Tagline className={`mt-4 text-center ${taglineClassName}`} />
    </div>
  );
}

/** Horizontal mark — monogram left, wordmark right. Use in headers / footer. */
export function BrandLockupHorizontal({
  monogramClassName = "h-9 w-auto",
  wordmarkSize = "md",
  className = "",
  asLink = false,
}) {
  const Wrap = asLink ? "a" : "span";
  return (
    <Wrap className={`inline-flex items-center gap-3 ${className}`}>
      <Monogram className={monogramClassName} />
      <Wordmark size={wordmarkSize} />
    </Wrap>
  );
}

// ---- Legacy exports (raster lockup PNGs) ----
// Retained so any stale imports keep working; new code should use the
// primitives above.
export function LogoFull({ className = "h-40 w-auto", ...rest }) {
  return <img src={fullTransparent} alt="Omertàbella — Private Influence. Public Power." className={className} {...rest} />;
}
export function LogoWordmark({ className = "h-12 w-auto", ...rest }) {
  return <img src={wordmarkTransparent} alt="Omertàbella" className={className} {...rest} />;
}
export function LogoMonogram({ className = "h-16 w-16", ...rest }) {
  return <img src={monoTransparent} alt="Omertàbella" className={className} {...rest} />;
}
